
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { PendingApproval } from './PendingApproval';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, LogIn, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';

interface RequireAuthProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

export const RequireAuth = ({ children, requiredRoles }: RequireAuthProps) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasRole, setHasRole] = useState<boolean | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [sessionError, setSessionError] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  const checkUserRole = useCallback(async () => {
    if (!user) {
      setRoleLoading(false);
      return;
    }

    setSessionError(false);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching user role:', error);
        // The profile couldn't be read (expired session/network). PendingApproval
        // would mislead users into thinking they await approval, so surface an
        // explicit session problem instead.
        setSessionError(true);
        setHasRole(false);
        setUserRole(null);
      } else {
        setUserRole(data.role);
        setHasRole(!!data.role);

        // Check if the user has the required role to access this page
        if (requiredRoles && data.role && !requiredRoles.includes(data.role)) {
          console.log('User does not have required role', requiredRoles, data.role);
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Exception fetching user role:', error);
      setSessionError(true);
      setHasRole(false);
    } finally {
      setRoleLoading(false);
    }
  }, [user, requiredRoles, navigate]);

  useEffect(() => {
    checkUserRole();
  }, [checkUserRole]);

  // Determine if the user needs to verify their email or phone
  const emailUnverified = user && user.email && !user.email_confirmed_at;
  const phoneUnverified = user && user.phone && !user.phone_confirmed_at;
  const needsVerification = emailUnverified || phoneUnverified;

  const handleInitiateVerification = () => {
    setShowVerificationPrompt(true);
    navigate('/verification');
  };

  if (loading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-eo-blue"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const handleRetryRole = () => {
    setRoleLoading(true);
    checkUserRole();
  };

  if (hasRole === false) {
    if (sessionError) {
      return (
        <div className="flex min-h-screen items-center justify-center px-4">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-2 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                <AlertCircle className="h-6 w-6 text-amber-600" />
              </div>
              <CardTitle className="text-xl font-bold">Session expired</CardTitle>
              <CardDescription className="text-base">
                Your login session could not be verified. This usually happens after a
                long break — signing in again will fix it.
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex flex-col gap-2">
              <Button onClick={handleSignOut} className="w-full">
                <LogIn className="h-4 w-4 mr-2" />
                Sign in again
              </Button>
              <Button onClick={handleRetryRole} variant="outline" className="w-full">
                <RefreshCcw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }
    return <PendingApproval />;
  }

  return (
    <>
      {needsVerification && !showVerificationPrompt && (
        <Alert variant="warning" className="bg-amber-50 border-amber-200 mb-4 mx-4 mt-4">
          <AlertCircle className="h-4 w-4 text-amber-500 mr-2" />
          <div className="flex flex-col sm:flex-row sm:items-center w-full justify-between">
            <AlertDescription className="text-amber-700">
              Please verify your {emailUnverified && phoneUnverified 
                ? 'email and phone number' 
                : emailUnverified 
                  ? 'email' 
                  : 'phone number'
              } to ensure you can use all features.
            </AlertDescription>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2 sm:mt-0 bg-white border-amber-300 hover:bg-amber-50 text-amber-700"
              onClick={() => navigate('/profile')}
            >
              Verify Now
            </Button>
          </div>
        </Alert>
      )}
      {children}
    </>
  );
};
